import React, { useState, useCallback } from "react";
import { useLoading } from "../../contexts/LoadingContext";
import { showToast } from "../../components/Toaster";
import TripService from "../../services/TripService";
import Modal from "../Modal/Modal";
import ContainerType from "../ContainerType/ContainerType";
import { transformTruckNumber } from "../../utils/general";
import {
  ModalSection,
  ModalInfoItem,
  ModalInfoGrid,
  ModalFormGroup,
  ModalButton,
  ModalError,
  ModalWarning,
  ModalFooter,
  ModalIcons,
} from "../Modal/ModalLayout";
import "./ForceEndTripModal.scss";

interface ForceEndTripModalProps {
  isOpen: boolean;
  onClose: () => void;
  tripData: {
    jobBreakupDetailId: number;
    jobCode: string;
    containerNumber: string;
    containerSiTy: string;
    truckIdName: string;
    driverIdName: string;
    transporterIdName: string;
    pickedAt: string;
    tripStatus: string;
    source: string;
    destination: string;
  } | null;
  onEndTripSuccess: () => void;
}

const ForceEndTripModal: React.FC<ForceEndTripModalProps> = ({
  isOpen,
  onClose,
  tripData,
  onEndTripSuccess,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [showValidationError, setShowValidationError] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [selectedTripId, setSelectedTripId] = useState<number | null>(null);
  const loader = useLoading();

  // Reset state when modal opens/closes, but preserve for same trip
  React.useEffect(() => {
    if (tripData && isOpen) {
      // Only reset form fields if this is a new trip (different tripId)
      // or if the modal is opening for the first time
      const shouldReset =
        !selectedTripId || selectedTripId !== tripData.jobBreakupDetailId;

      console.log("ForceEndTripModal effect triggered:", {
        isOpen,
        tripId: tripData.jobBreakupDetailId,
        selectedTripId,
        shouldReset,
      });

      if (shouldReset) {
        console.log("Resetting form fields for new trip");
        setError(null);
        setShowValidationError(false);
        setConfirmationText("");
        setSelectedTripId(tripData.jobBreakupDetailId);
      } else {
        console.log("Preserving form fields for existing trip");
      }
    }
  }, [isOpen, tripData?.jobBreakupDetailId]); // Only depend on isOpen and tripId, not the entire tripData object

  // Reset selectedTripId when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setSelectedTripId(null);
    }
  }, [isOpen]);

  const handleEndTrip = useCallback(async () => {
    if (!tripData) return;

    // Reset error state
    setError(null);
    setShowValidationError(false);

    // Validate confirmation text
    if (confirmationText.trim().toLowerCase() !== "end trip") {
      setShowValidationError(true);
      setError("Please type 'end trip' to confirm");
      return;
    }

    try {
      loader.setLoading(true);

      const response = await TripService.endTrip({
        tripId: tripData.jobBreakupDetailId,
      });

      if (response.data.success || !response.data.error) {
        const tripData = response.data.data;

        // Show success message with trip details
        const successMessage = `Trip ended successfully! Trip ID: ${tripData.jobBreakupDetailId}, Container: ${tripData.containerNumber}, Status: ${tripData.tripStatus}`;
        showToast(successMessage, "success");

        // Log the complete response for debugging
        console.log("Trip ended successfully:", {
          tripId: tripData.jobBreakupDetailId,
          containerNumber: tripData.containerNumber,
          tripStatus: tripData.tripStatus,
          jobStatus: tripData.jobStatus,
          endDate: tripData.endDate,
          transporter: tripData.transporterIdName?.name,
          truck: tripData.truckIdName?.name,
          driver: tripData.driverIdName?.name,
        });

        onEndTripSuccess();
        onClose();
      } else {
        const errorMessage = response.data.message || "Failed to end trip";
        showToast(errorMessage, "error");
        console.error("API error ending trip:", response.data);
      }
    } catch (error: any) {
      console.error("Error ending trip:", error);

      let errorMessage = "Failed to end trip";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast(errorMessage, "error");
    } finally {
      loader.setLoading(false);
    }
  }, [tripData, confirmationText, loader, onEndTripSuccess, onClose]);

  const handleConfirmationTextChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setConfirmationText(value);

    // Clear validation error when user starts typing
    if (showValidationError) {
      setShowValidationError(false);
    }
  };

  const isFormValid = () => {
    return confirmationText.trim().toLowerCase() === "end trip";
  };

  if (!tripData) return null;

  // Extract container size and type from containerSiTy
  const [size, type] = tripData.containerSiTy.split(" ");

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Force End Trip"
      className="component_modal_container force_end_trip_modal"
      size="lg"
      footer={
        <ModalFooter>
          <ModalButton variant="secondary" onClick={onClose}>
            Cancel
          </ModalButton>
          <ModalButton
            variant="danger"
            onClick={handleEndTrip}
            disabled={!isFormValid()}
          >
            Force End Trip
          </ModalButton>
        </ModalFooter>
      }
    >
      <div className="space-y-6">
        {/* Trip Information */}
        <ModalSection title="Trip Details">
          <ModalInfoGrid>
            <ModalInfoItem label="Job Code" value={tripData.jobCode} />
            <ModalInfoItem
              label="Trip ID"
              value={tripData.jobBreakupDetailId}
            />
          </ModalInfoGrid>
        </ModalSection>

        {/* Container Information */}
        <ModalSection title="Container Details">
          <ModalInfoGrid>
            <ModalInfoItem
              label="Container Number"
              value={tripData.containerNumber.toUpperCase()}
            />
            <ModalInfoItem
              label="Type"
              value={<ContainerType size={size} type={type} />}
            />
            <ModalInfoItem
              label="Status"
              value={
                tripData.tripStatus === "IN_TRANSIT"
                  ? "Picked"
                  : tripData.tripStatus
              }
            />
            <ModalInfoItem label="Picked At" value={tripData.pickedAt} />
          </ModalInfoGrid>
        </ModalSection>

        {/* Driver & Transporter Information */}
        <ModalSection title="Driver & Transporter Details">
          <ModalInfoGrid>
            <ModalInfoItem label="Driver" value={tripData.driverIdName} />
            <ModalInfoItem
              label="Truck"
              value={
                tripData.truckIdName !== "N/A"
                  ? transformTruckNumber(tripData.truckIdName)
                  : "N/A"
              }
            />
            <ModalInfoItem
              label="Transporter"
              value={tripData.transporterIdName}
              className="col-span-2"
            />
          </ModalInfoGrid>
        </ModalSection>

        {/* Route Information */}
        <ModalSection title="Route Information">
          <ModalInfoGrid>
            <ModalInfoItem label="Source" value={tripData.source} />
            <ModalInfoItem label="Destination" value={tripData.destination} />
          </ModalInfoGrid>
        </ModalSection>

        {/* Warning Section - Moved to bottom */}
        <ModalWarning
          title="Critical Action Warning"
          description="This action will permanently end the trip and cannot be undone."
        />

        {/* Confirmation Section */}
        <ModalSection title="Confirmation Required">
          <div className="space-y-4">
            <p className="text-sm text-[#59585b]">
              To confirm that you want to force end this trip, please type{" "}
              <strong>"end trip"</strong> in the field below:
            </p>
            <div className="form-group">
              <div className="form-field">
                <label className="required_field_label">
                  Confirmation Text
                </label>
                <input
                  type="text"
                  value={confirmationText}
                  onChange={handleConfirmationTextChange}
                  placeholder="Type 'end trip' to confirm"
                  className={showValidationError ? "invalid_field" : ""}
                />
              </div>
              <div className="form-help warning">
                Type 'end trip' to confirm this action
              </div>
            </div>
          </div>
        </ModalSection>

        {/* Error Message */}
        {showValidationError && error && <ModalError message={error} />}
      </div>
    </Modal>
  );
};

export default ForceEndTripModal;
